import { yupResolver } from "@hookform/resolvers/yup";
import { errorHandler } from "_services/errorHandler";
import {
  useDriverServiceV1CreateDriverMutation,
  useDriverServiceV1CreateDriverDraftMutation,
  useDriverServiceV1GetDriverDetailQuery,
  useDriverServiceV1GetDriverDocumentsQuery,
  useDriverServiceV1UpdateDriverDraftMutation,
  useDriverServiceV1UpdateDriverMutation,
  DriverServiceV1UpdateDriverBody,
  DriverServiceV1UpdateDriverDraftBody,
  TransportResourceCreateDriverReq,
  TransportResourceCreateDriverDraftReq,
  TransportResourceGetDriverDetailRes,
} from "_services/modules/transport-resource/driver";

import {
  ExtendedDriverDetailRes,
  ExtendedDriverDocument,
  ExtendedUpdateDriverBody as ExtendedUpdateBody,
  ExtendedUpdateDriverDraftBody as ExtendedUpdateDraftBody,
  ExtendedCreateDriverReq as ExtendedCreateReq,
  ExtendedCreateDriverDraftReq as ExtendedCreateDraftReq,
} from "_interfaces/driver.interfaces";
import { useDriverDocumentServiceV1UploadDriverDocumentMutation } from "_services/modules/transport-resource/driver-document";

const mapStatusToBackend = (status?: string): string => {
  if (!status) return "Available";
  const statusLower = status.toLowerCase();
  if (statusLower === "inactive") {
    return "Inactive";
  }
  if (statusLower === "active" || statusLower === "available") {
    return "Available";
  }
  return "Available";
};

const normalizeRelation = (relation?: string): string => {
  if (!relation) return "";
  const r = relation.toLowerCase();
  if (r === "mother") return "Mother";
  if (r === "father") return "Father";
  if (r === "spouse") return "Spouse";
  if (r === "son") return "Son";
  if (r === "daughter") return "Daughter";
  if (r === "friend") return "Friend";
  return relation;
};

const normalizeGender = (gender?: string): string => {
  if (!gender) return "";
  const g = gender.toLowerCase();
  if (g === "male" || g === "men") return "Men";
  if (g === "female" || g === "women") return "Women";
  return gender;
};

const normalizeMaritalStatus = (status?: string): string => {
  if (!status) return "";
  const s = status.toLowerCase();
  if (s === "single") return "Single";
  if (s === "married") return "Married";
  if (s === "divorced") return "Divorced";
  return status;
};
import {
  DriverDocumentType,
  DriverFormValues,
  UploadedDriverDocument,
  DriverDocumentErrors,
} from "_interfaces/driver.interfaces";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";
import { validateUploadFile, translateFileValidationError, DOCUMENT_UPLOAD_OPTIONS } from "_helper/fileValidation";
import { useAppTranslation } from "locale/useAppTranslation";
import { useVendorSelectOptions } from "modules/master-data/vendor/list/useVendorList";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { driverListPageRouteName } from "modules/master-data/driver/index.page";
import { REQUIRED_DRIVER_DOCUMENTS } from "modules/master-data/driver/constants/options";

const formatPhoneNumber = (countryCode: string, number?: string): string | undefined => {
  if (!number) return undefined;
  let cleanNumber = number.replace(/\s+/g, "");
  
  if (cleanNumber.startsWith("+")) {
    cleanNumber = cleanNumber.slice(1);
  }
  
  const ccWithoutPlus = countryCode.replace("+", "");
  if (cleanNumber.startsWith(ccWithoutPlus)) {
    cleanNumber = cleanNumber.slice(ccWithoutPlus.length);
  }
  
  if (cleanNumber.startsWith("0")) {
    cleanNumber = cleanNumber.slice(1);
  }
  
  return `${countryCode}${cleanNumber}`;
};

const schema = yup
  .object({
    driverName: yup.string().required("Driver name is required"),
    ktpNumber: yup
      .string()
      .matches(/^\d{16}$/, "KTP number must be 16 digits")
      .required("KTP number is required"),
    phoneCountryCode: yup.string().required(),
    phoneNumber: yup.string().required("Phone number is required"),
    associatedVendor: yup.string().required("Associated vendor is required"),
    domicileAddress: yup.string().required("Domicile address is required"),
    citizenAddress: yup.string().required("Citizen address is required"),
    isSameAddress: yup.boolean().default(false),
    status: yup
      .mixed<"active" | "inactive">()
      .oneOf(["active", "inactive"])
      .required(),
    emergencyName: yup.string().required("Emergency contact name is required"),
    emergencyRelation: yup.string().required("Emergency relation is required"),
    emergencyPhoneCountryCode: yup.string().required(),
    emergencyPhoneNumber: yup
      .string()
      .required("Emergency contact phone is required"),
    bankName: yup.string().required("Bank name is required"),
    bankAccountNumber: yup.string().required("Bank account number is required"),
    bankAccountHolder: yup.string().required("Account holder name is required"),
    licenseType: yup.string().required("License type is required"),
    licenseNumber: yup.string().required("License number is required"),
    licenseExpiryDate: yup.string().required("License expiry date is required"),
    email: yup
      .string()
      .email("Please enter a valid email")
      .required("Email is required"),
    creationMethod: yup
      .mixed<"invitation" | "manual">()
      .oneOf(["invitation", "manual"])
      .required(),
    password: yup.string().when("creationMethod", {
      is: "manual",
      then: (currentSchema) => currentSchema.required("Password is required"),
      otherwise: (currentSchema) => currentSchema.notRequired(),
    }),
    driverId: yup.string().optional(),
    joinDate: yup.string().optional(),
  })
  .required();

const useCreateDriverForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId?: string }>();
  const location = useLocation();
  const { t } = useAppTranslation("driver");
  const { vendorOptions, isLoadingVendorOptions, onSearchVendors } =
    useVendorSelectOptions(5);
  const [createDriver, { isLoading: isCreatingDriver }] =
    useDriverServiceV1CreateDriverMutation();
  const [createDriverDraft, { isLoading: isCreatingDriverDraft }] =
    useDriverServiceV1CreateDriverDraftMutation();
  const [updateDriverDraft, { isLoading: isUpdatingDriverDraft }] =
    useDriverServiceV1UpdateDriverDraftMutation();
  const [updateDriver, { isLoading: isUpdatingDriverActive }] =
    useDriverServiceV1UpdateDriverMutation();
  const [uploadDriverDocument, { isLoading: isUploadingDriverPicture }] =
    useDriverDocumentServiceV1UploadDriverDocumentMutation();

  const [isDetailHydrated, setIsDetailHydrated] = useState(false);
  const [isDocsHydrated, setIsDocsHydrated] = useState(false);

  const { data: driverDetail, isLoading: isLoadingDetail } =
    useDriverServiceV1GetDriverDetailQuery(
      { id: draftId! },
      { skip: !draftId }
    );
  const { data: driverDocuments, isLoading: isLoadingDocs } =
    useDriverServiceV1GetDriverDocumentsQuery(
      { id: draftId! },
      { skip: !draftId }
    );

  const form = useForm<DriverFormValues>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      driverName: "",
      ktpNumber: "",
      phoneCountryCode: "+62",
      phoneNumber: "",
      associatedVendor: "",
      domicileAddress: "",
      citizenAddress: "",
      isSameAddress: false,
      status: "active",
      emergencyName: "",
      emergencyRelation: "",
      emergencyPhoneCountryCode: "+62",
      emergencyPhoneNumber: "",
      bankName: "",
      bankAccountNumber: "",
      bankAccountHolder: "",
      licenseType: "",
      licenseNumber: "",
      licenseExpiryDate: "",
      gender: "",
      maritalStatus: "",
      email: "",
      creationMethod: "invitation",
      password: "",
      driverId: "",
      joinDate: "",
    },
  });

  const [openSections, setOpenSections] = useState({
    driverProfile: true,
    emergencyContacts: false,
    financialDocuments: false,
    accountAccess: false,
  });

  const [driverPictureFile, setDriverPictureFile] = useState<File | undefined>(
    undefined
  );
  const [driverPictureName, setDriverPictureName] = useState("");
  const [driverPictureUrl, setDriverPictureUrl] = useState("");
  const [driverPictureError, setDriverPictureError] = useState("");
  const [documentFiles, setDocumentFiles] = useState<
    Record<DriverDocumentType, UploadedDriverDocument>
  >({
    skck: { fileName: "", fileUrl: "" },
    ktp: { fileName: "", fileUrl: "" },
    kk: { fileName: "", fileUrl: "" },
    sim: { fileName: "", fileUrl: "" },
    domicile_letter: { fileName: "", fileUrl: "" },
  });

  const [expiryDates, setExpiryDates] = useState<
    Record<DriverDocumentType, string>
  >({
    skck: "",
    ktp: "",
    kk: "",
    sim: "",
    domicile_letter: "",
  });

  const handleExpiryDateChange = (type: DriverDocumentType, date: string) => {
    setExpiryDates((prev) => ({ ...prev, [type]: date }));
  };

  const validatePicture = useCallback((): boolean => {
    const hasPicture = Boolean(driverPictureFile || driverPictureUrl);
    if (!hasPicture) {
      const errorMsg =
        t("error-driver-picture-required") ||
        "Driver profile photo is required";
      setDriverPictureError(errorMsg);
      toast.error(errorMsg);
      setOpenSections((current) => ({
        ...current,
        driverProfile: true,
      }));
      return false;
    }
    setDriverPictureError("");
    return true;
  }, [driverPictureFile, driverPictureUrl, t]);

  const [documentErrors, setDocumentErrors] = useState<DriverDocumentErrors>({});

  const validateDocuments = useCallback((): boolean => {
    const errors: DriverDocumentErrors = {};
    const missingLabels: string[] = [];

    REQUIRED_DRIVER_DOCUMENTS.forEach((doc) => {
      const entry = documentFiles[doc.type];
      const isUploaded = Boolean(entry?.fileUrl || entry?.pendingFile);
      if (!isUploaded) {
        const docName =
          doc.type === "domicile_letter"
            ? t("label-document-domicile") || "Domicile Letter"
            : doc.defaultLabel;
        errors[doc.type] =
          t("error-document-required", { doc: docName }) ||
          `${docName} document is required`;
        missingLabels.push(docName);
      }
    });

    setDocumentErrors(errors);

    if (missingLabels.length > 0) {
      toast.error(
        t("toast-missing-documents", { docs: missingLabels.join(", ") }) ||
          `${missingLabels.join(", ")} document(s) required`
      );
      setOpenSections((current) => ({
        ...current,
        financialDocuments: true,
      }));
      return false;
    }

    return true;
  }, [documentFiles, t]);

  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (driverDetail && draftId && !isDetailHydrated) {
      const detail = driverDetail as ExtendedDriverDetailRes;
      form.reset({
        driverName: driverDetail.full_name || "",
        ktpNumber: driverDetail.ktp_number || "",
        phoneCountryCode: "+62",
        phoneNumber: (driverDetail.phone_number || "").replace("+62", ""),
        associatedVendor: driverDetail.vendor_id || "",
        domicileAddress: driverDetail.domicile_address || "",
        citizenAddress: driverDetail.citizen_address || "",
        isSameAddress:
          driverDetail.citizen_address === driverDetail.domicile_address,
        status:
          driverDetail.status?.toLowerCase() === "available" ||
          driverDetail.status?.toLowerCase() === "active"
            ? "active"
            : "inactive",
        emergencyName: driverDetail.emergency_contact_name || "",
        emergencyRelation: normalizeRelation(driverDetail.emergency_contact_relation || ""),
        emergencyPhoneCountryCode: "+62",
        emergencyPhoneNumber: (driverDetail.emergency_contact_phone || "").replace("+62", ""),
        bankName: driverDetail.bank_name || "",
        bankAccountNumber: driverDetail.bank_account_number || "",
        bankAccountHolder: driverDetail.bank_account_holder || "",
        licenseType: driverDetail.license_type || "",
        licenseNumber: driverDetail.license_number || "",
        licenseExpiryDate: driverDetail.license_expiry_date || "",
        gender: normalizeGender(driverDetail.gender || ""),
        maritalStatus: normalizeMaritalStatus(driverDetail.marital_status || ""),
        email: driverDetail.email || "",
        creationMethod: "invitation",
        password: "",
        driverId: detail.driver_code || detail.driver_id || "",
        joinDate: detail.join_date || "",
      });
      setDriverPictureUrl(detail.driver_picture_url || "");
      setIsDetailHydrated(true);
    }
  }, [driverDetail, draftId, form, isDetailHydrated]);

  useEffect(() => {
    if (!draftId || isDocsHydrated) return;
    if (isLoadingDetail || isLoadingDocs) return;

    const detail = driverDetail as ExtendedDriverDetailRes;
    const docs = detail?.documents || driverDocuments?.documents;
    if (docs) {
      const files: Record<DriverDocumentType, UploadedDriverDocument> = {
        skck: { fileName: "", fileUrl: "" },
        ktp: { fileName: "", fileUrl: "" },
        kk: { fileName: "", fileUrl: "" },
        sim: { fileName: "", fileUrl: "" },
        domicile_letter: { fileName: "", fileUrl: "" },
      };
      const expDates: Record<DriverDocumentType, string> = {
        skck: "",
        ktp: "",
        kk: "",
        sim: "",
        domicile_letter: "",
      };

      docs.forEach((doc: any) => {
        const docType = doc.document_type || doc.type;
        const docUrl = doc.document_url || doc.file_url;
        if (docType) {
          const typeLower = docType.toLowerCase().replace(" ", "_") as DriverDocumentType;
          if (["skck", "ktp", "kk", "sim", "domicile_letter"].includes(typeLower)) {
            if (docUrl) {
              files[typeLower] = {
                fileName: docUrl.split("/").pop() || docType,
                fileUrl: docUrl,
              };
            }
            expDates[typeLower] = doc.expiry_date || "";
          }
        }
      });

      setDocumentFiles(files);
      setExpiryDates(expDates);
      setIsDocsHydrated(true);
    }
  }, [driverDetail, driverDocuments, isLoadingDetail, isLoadingDocs, draftId, isDocsHydrated]);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: draftId
          ? t("page-title-edit-draft") || "Edit Driver Draft"
          : t("page-title-create") || "Create Driver Data",
        hasBackButton: true,
        customBackEvent: "driver-create-back",
      })
    );

    return () => {
      dispatch(
        setGlobalComponent({
          title: t("page-title-list") || "Driver",
          hasBackButton: false,
          customBackEvent: undefined,
        })
      );
    };
  }, [dispatch, t, draftId]);

  useEffect(() => {
    const handleBack = () => {
      if (form.formState.isDirty) {
        setShowCancelModal(true);
      } else {
        if (location.state?.from === "draft") {
          navigate("/master-data/driver/draft");
        } else {
          navigate(-1);
        }
      }
    };

    window.addEventListener("driver-create-back", handleBack);
    return () => window.removeEventListener("driver-create-back", handleBack);
  }, [form.formState.isDirty, navigate, location.state?.from]);

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const handleNextFromProfile = async () => {
    const isValid = await form.trigger([
      "driverName",
      "ktpNumber",
      "phoneNumber",
      "associatedVendor",
      "domicileAddress",
      "citizenAddress",
      "status",
    ]);

    if (!isValid) {
      return;
    }

    setOpenSections((current) => ({
      ...current,
      driverProfile: false,
      emergencyContacts: true,
    }));
  };

  const handleNextFromEmergency = async () => {
    const isValid = await form.trigger([
      "emergencyName",
      "emergencyRelation",
      "emergencyPhoneNumber",
    ]);

    if (!isValid) {
      return;
    }

    setOpenSections((current) => ({
      ...current,
      emergencyContacts: false,
      financialDocuments: true,
    }));
  };

  const handleNextFromFinancial = async () => {
    const isValid = await form.trigger([
      "bankName",
      "bankAccountNumber",
      "bankAccountHolder",
      "licenseType",
      "licenseNumber",
      "licenseExpiryDate",
    ]);

    const isDocsValid = validateDocuments();

    if (!isValid || !isDocsValid) {
      return;
    }

    setOpenSections((current) => ({
      ...current,
      financialDocuments: false,
      accountAccess: true,
    }));
  };
  const handleDriverPictureChange = (file?: File) => {
    if (!file) {
      setDriverPictureFile(undefined);
      setDriverPictureName("");
      setDriverPictureUrl("");
      return;
    }

    const validationError = validateUploadFile(file, {
      allowedExtensions: ["jpg", "jpeg", "png"],
      maxSizeMB: 5,
    });
    if (validationError) {
      const message = translateFileValidationError(t, validationError);
      setDriverPictureError(message);
      toast.error(message);
      return;
    }

    setDriverPictureError("");
    setDriverPictureFile(file);
    setDriverPictureName(file.name);
    setDriverPictureUrl(URL.createObjectURL(file));
  };
  const uploadPictureIfNeeded = async (): Promise<string | undefined> => {
    if (!driverPictureFile) return driverPictureUrl || undefined;

    const formData = new FormData();
    formData.append("file", driverPictureFile);

    const uploadRes = await uploadDriverDocument({
      apiHttpBody: formData as any,
    }).unwrap();

    const url = uploadRes.file_url ?? "";
    setDriverPictureUrl(url);
    setDriverPictureFile(undefined);
    return url || undefined;
  };

  const handleDocumentFileChange = async (
    type: DriverDocumentType,
    file?: File
  ) => {
    setDocumentErrors((prev) => {
      if (!prev[type]) return prev;
      const next = { ...prev };
      delete next[type];
      return next;
    });

    if (!file) {
      setDocumentFiles((current) => ({
        ...current,
        [type]: { fileName: "", fileUrl: "", pendingFile: undefined },
      }));
      return;
    }

    const validationError = validateUploadFile(file, DOCUMENT_UPLOAD_OPTIONS);
    if (validationError) {
      const message = translateFileValidationError(t, validationError);
      setDocumentErrors((prev) => ({ ...prev, [type]: message }));
      toast.error(message);
      return;
    }

    setDocumentErrors((prev) => {
      if (!prev[type]) return prev;
      const next = { ...prev };
      delete next[type];
      return next;
    });
    setDocumentFiles((current) => ({
      ...current,
      [type]: { fileName: file.name, fileUrl: "", pendingFile: file },
    }));
  };

  const uploadDocumentsIfNeeded = async (): Promise<
    Record<DriverDocumentType, UploadedDriverDocument>
  > => {
    const entries = Object.entries(documentFiles) as [
      DriverDocumentType,
      UploadedDriverDocument,
    ][];
    const next: Record<DriverDocumentType, UploadedDriverDocument> = {
      ...documentFiles,
    };

    await Promise.all(
      entries.map(async ([type, entry]) => {
        if (!entry.pendingFile || entry.fileUrl) return;
        const formData = new FormData();
        formData.append("file", entry.pendingFile);

        const uploadRes = await uploadDriverDocument({
          apiHttpBody: formData as any,
        }).unwrap();

        next[type] = {
          fileName: uploadRes.file_name ?? entry.pendingFile.name,
          fileUrl: uploadRes.file_url ?? "",
          pendingFile: undefined,
        };
      })
    );

    setDocumentFiles(next);
    return next;
  };

  const handleSaveDraft = async () => {
    let pictureUrl: string | undefined;
    try {
      pictureUrl = await uploadPictureIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }

    let uploadedFiles: Record<DriverDocumentType, UploadedDriverDocument>;
    try {
      uploadedFiles = await uploadDocumentsIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }

    const values = form.getValues();
    const documents = Object.entries(uploadedFiles)
      .filter(([, uploadedDocument]) => Boolean(uploadedDocument.fileUrl))
      .map(([type, uploadedDocument]) => ({
        document_type: type === "domicile_letter" ? "Domicile Letter" : type.toUpperCase(),
        document_url: uploadedDocument.fileUrl,
        expiry_date: expiryDates[type as DriverDocumentType] || undefined,
      }));

    try {
      if (draftId) {
        const updateDraftBody: ExtendedUpdateDraftBody = {
          full_name: values.driverName || undefined,
          ktp_number: values.ktpNumber || undefined,
          phone_number: formatPhoneNumber(values.phoneCountryCode, values.phoneNumber),
          email: values.email || undefined,
          vendor_id: values.associatedVendor || undefined,
          gender: values.gender || undefined,
          marital_status: values.maritalStatus || undefined,
          citizen_address: values.citizenAddress || undefined,
          domicile_address: values.domicileAddress || undefined,
          license_type: values.licenseType || undefined,
          license_number: values.licenseNumber || undefined,
          license_expiry_date: values.licenseExpiryDate || undefined,
          bank_name: values.bankName || undefined,
          bank_account_number: values.bankAccountNumber || undefined,
          bank_account_holder: values.bankAccountHolder || undefined,
          documents,
          driver_picture_url: pictureUrl || undefined,
          emergency_contact_name: values.emergencyName || undefined,
          emergency_contact_phone: formatPhoneNumber(values.emergencyPhoneCountryCode, values.emergencyPhoneNumber),
          emergency_contact_relation: values.emergencyRelation || undefined,
          driver_code: values.driverId || undefined,
          join_date: values.joinDate || undefined,
        };

        await updateDriverDraft({
          id: draftId,
          driverServiceV1UpdateDriverDraftBody: updateDraftBody as unknown as DriverServiceV1UpdateDriverDraftBody,
        }).unwrap();
        toast.success(
          t("toast-update-success") || "Draft updated successfully"
        );
      } else {
        const createDraftReq: ExtendedCreateDraftReq = {
          full_name: values.driverName || undefined,
          ktp_number: values.ktpNumber || undefined,
          phone_number: formatPhoneNumber(values.phoneCountryCode, values.phoneNumber),
          email: values.email || undefined,
          driver_picture_url: pictureUrl || undefined,
          gender: values.gender || undefined,
          marital_status: values.maritalStatus || undefined,
          creation_method:
            values.creationMethod === "manual" ? "manual" : "invitation",
          password:
            values.creationMethod === "manual" && values.password
              ? values.password
              : undefined,
          vendor_id: values.associatedVendor || undefined,
          citizen_address: values.citizenAddress || undefined,
          domicile_address: values.domicileAddress || undefined,
          license_type: values.licenseType || undefined,
          license_number: values.licenseNumber || undefined,
          license_expiry_date: values.licenseExpiryDate || undefined,
          bank_name: values.bankName || undefined,
          bank_account_number: values.bankAccountNumber || undefined,
          bank_account_holder: values.bankAccountHolder || undefined,
          documents,
          emergency_contact_name: values.emergencyName || undefined,
          emergency_contact_phone: formatPhoneNumber(values.emergencyPhoneCountryCode, values.emergencyPhoneNumber),
          emergency_contact_relation: values.emergencyRelation || undefined,
          driver_code: values.driverId || undefined,
          join_date: values.joinDate || undefined,
        };

        await createDriverDraft({
          transportResourceCreateDriverDraftReq: createDraftReq as unknown as TransportResourceCreateDriverDraftReq,
        }).unwrap();
        toast.success(t("toast-draft-saved"));
      }

      if (location.state?.from === "draft") {
        navigate("/master-data/driver/draft");
      } else {
        navigate("/master-data/driver");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleSave = async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      toast.error(t("toast-complete-required-fields"));
      return;
    }

    const isPicValid = validatePicture();
    if (!isPicValid) {
      return;
    }

    const isDocsValid = validateDocuments();
    if (!isDocsValid) {
      return;
    }

    let pictureUrl: string | undefined;
    try {
      pictureUrl = await uploadPictureIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }

    let uploadedFiles: Record<DriverDocumentType, UploadedDriverDocument>;
    try {
      uploadedFiles = await uploadDocumentsIfNeeded();
    } catch (error) {
      errorHandler(error);
      return;
    }

    const values = form.getValues();
    const documents = Object.entries(uploadedFiles)
      .filter(([, uploadedDocument]) => Boolean(uploadedDocument.fileUrl))
      .map(([type, uploadedDocument]) => ({
        document_type: type === "domicile_letter" ? "Domicile Letter" : type.toUpperCase(),
        document_url: uploadedDocument.fileUrl,
        expiry_date: expiryDates[type as DriverDocumentType] || undefined,
      }));

    try {
      if (draftId) {
        const updateBody: ExtendedUpdateBody = {
          full_name: values.driverName,
          ktp_number: values.ktpNumber,
          phone_number: formatPhoneNumber(values.phoneCountryCode, values.phoneNumber) || "",
          email: values.email,
          vendor_id: values.associatedVendor,
          citizen_address: values.citizenAddress,
          domicile_address: values.domicileAddress,
          license_type: values.licenseType,
          license_number: values.licenseNumber,
          license_expiry_date: values.licenseExpiryDate,
          bank_name: values.bankName,
          bank_account_number: values.bankAccountNumber,
          bank_account_holder: values.bankAccountHolder,
          status: "Available",
          documents,
          driver_picture_url: pictureUrl || undefined,
          emergency_contact_name: values.emergencyName,
          emergency_contact_phone: formatPhoneNumber(values.emergencyPhoneCountryCode, values.emergencyPhoneNumber) || "",
          emergency_contact_relation: values.emergencyRelation,
          join_date: values.joinDate || undefined,
        };

        await updateDriver({
          id: draftId,
          driverServiceV1UpdateDriverBody: updateBody as unknown as DriverServiceV1UpdateDriverBody,
        }).unwrap();
        toast.success(
          t("toast-update-success") || "Driver updated successfully"
        );
      } else {
        const createReq: ExtendedCreateReq = {
          full_name: values.driverName,
          ktp_number: values.ktpNumber,
          phone_number: formatPhoneNumber(values.phoneCountryCode, values.phoneNumber) || "",
          email: values.email,
          driver_picture_url: pictureUrl || undefined,
          gender: values.gender || undefined,
          marital_status: values.maritalStatus || undefined,
          creation_method:
            values.creationMethod === "manual" ? "manual" : "invitation",
          password:
            values.creationMethod === "manual" ? values.password : undefined,
          vendor_id: values.associatedVendor,
          citizen_address: values.citizenAddress,
          domicile_address: values.domicileAddress,
          license_type: values.licenseType,
          license_number: values.licenseNumber,
          license_expiry_date: values.licenseExpiryDate,
          bank_name: values.bankName,
          bank_account_number: values.bankAccountNumber,
          bank_account_holder: values.bankAccountHolder,
          documents,
          emergency_contact_name: values.emergencyName,
          emergency_contact_phone: formatPhoneNumber(values.emergencyPhoneCountryCode, values.emergencyPhoneNumber) || "",
          emergency_contact_relation: values.emergencyRelation,
          join_date: values.joinDate || undefined,
        };

        await createDriver({
          transportResourceCreateDriverReq: createReq as unknown as TransportResourceCreateDriverReq,
        }).unwrap();
        toast.success(t("toast-create-success"));
      }

      navigate(driverListPageRouteName);
    } catch (error) {
      errorHandler(error);
    }
  };

  const isDirty =
    form.formState.isDirty ||
    Boolean(driverPictureName) ||
    Object.values(documentFiles).some((file) =>
      Boolean(file.fileUrl || file.pendingFile)
    );

  const handleDiscard = () => {
    setShowCancelModal(false);
    if (location.state?.from === "draft") {
      navigate("/master-data/driver/draft");
    } else {
      navigate(driverListPageRouteName);
    }
  };

  return {
    t,
    form,
    openSections,
    toggleSection,
    vendorOptions,
    isLoadingVendorOptions,
    isCreatingDriver:
      isCreatingDriver ||
      isCreatingDriverDraft ||
      isUpdatingDriverActive ||
      isUpdatingDriverDraft,
    isUploadingDriverPicture,
    onSearchVendors,
    driverPictureName,
    driverPictureUrl,
    driverPictureError,
    validatePicture,
    documentFiles,
    documentErrors,
    setDocumentErrors,
    validateDocuments,
    expiryDates,
    handleExpiryDateChange,
    isDirty,
    handleNextFromProfile,
    handleNextFromEmergency,
    handleNextFromFinancial,
    handleDriverPictureChange,
    handleDocumentFileChange,
    handleSaveDraft,
    handleSave,
    showCancelModal,
    setShowCancelModal,
    navigate,
    isLoading: isLoadingDetail || isLoadingDocs,
    handleDiscard,
  };
};

export default useCreateDriverForm;
